import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    Table,
    TableHeader,
    TableBody,
    TableRow,
    TableHead,
    TableCell,
} from '../../../../components/ui/Table';
import { StatusBadge } from '../../../../components/ui/StatusBadge';
import Pagination from '../../../../components/Pagination';
import ReportsController from '../../../../components/reportsController';
import { ClockingsPerUserPerDayView } from '../../../../models/attendance';
import * as ReportsApi from '../../../../api/Attendance/reports';
import { FetchUsers, getUser } from '../../../../api/General/account';
import { getBranches, getDepartments } from '../../../../api/General/Company';
import { getUsersCountries } from '../../../../api/General/Country';
import dateformat from 'dateformat';

type GroupedData = { [userId: string]: { [date: string]: any[] } };

const localDateKey = (dt: any): string => {
    const d = new Date(dt);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

function TypeBadge({ type, t }: { type: string; t: any }) {
    const lower = (type || '').toLowerCase();
    let variant: 'neutral' | 'success' | 'error' | 'warning' = 'neutral';
    if (lower.includes('in') || lower.includes('start')) variant = 'success';
    else if (lower.includes('out') || lower.includes('end')) variant = 'error';
    else if (lower.includes('break')) variant = 'warning';
    return <StatusBadge variant={variant} size="sm">{t(type)}</StatusBadge>;
}

function DeviceBadge({ device, t }: { device?: string; t: any }) {
    if (!device) return null;
    const lower = device.toLowerCase();
    let variant: 'neutral' | 'info' | 'success' | 'warning' = 'neutral';
    if (lower.includes('mobile') || lower.includes('app')) variant = 'info';
    else if (lower.includes('web')) variant = 'warning';
    else if (lower.includes('device')) variant = 'success';
    return <StatusBadge variant={variant} size="sm">{t(device)}</StatusBadge>;
}

export default function Clockings() {
    const { t } = useTranslation();
    const { userId, company, companyPrimaryColor } = useSelector((state: any) => ({
        userId: state.user.userId,
        company: state.user.company,
        companyPrimaryColor: state.user.companyPrimaryColor,
    }));

    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState<any[]>([]);
    const [flatItems, setFlatItems] = useState<any[]>([]);
    const [groups, setGroups] = useState<any[]>([]);
    const [groupedData, setGroupedData] = useState<GroupedData>({});

    const [offset, setOffset] = useState(0);
    const [flatOffset, setFlatOffset] = useState(0);
    const count = 5;
    const rowsPerPageFlat = 25;
    const [viewMode, setViewMode] = useState<'flat' | 'grouped'>('flat');

    const [users, setUsers] = useState<any[]>([]);
    const [users_, setUsers_] = useState<any[]>([]);
    const [selectedUsers, setSelectedUsers] = useState<any[]>([]);

    const [countries, setCountries] = useState<any[]>([]);
    const [selectedCountries, setSelectedCountries] = useState<any[]>([]);
    const [selectedCountryNames, setSelectedCountryNames] = useState<string[]>([]);

    const [branches, setBranches] = useState<any[]>([]);
    const [selectedBranches, setSelectedBranches] = useState<any[]>([]);
    const [selectedBrancheNames, setSelectedBrancheNames] = useState<string[]>([]);

    const [departments, setDepartments] = useState<any[]>([]);
    const [selectedDepartments, setSelectedDepartments] = useState<any[]>([]);
    const [selectedDepartmentNames, setSelectedDepartmentNames] = useState<string[]>([]);

    const [showFilters, setShowFiltersState] = useState(false);
    const [searchMethod, setSearchMethodState] = useState('AND');

    const [dateFrom, setDateFrom] = useState<Date>(new Date(new Date().getFullYear(), 0, 1));
    const [dateTo, setDateTo] = useState<Date>(new Date());

    const usersRef = useRef(users);
    usersRef.current = users;
    const users_Ref = useRef(users_);
    users_Ref.current = users_;

    // Cascading filter helpers
    const loadBranches = useCallback(async () => {
        const id = company?.id ?? 0;
        const res: any = await getBranches(id);
        if (res?.payload) {
            for (let i = 0; i < res.payload.length; i++) {
                res.payload[i].header = res.payload[i].name;
                res.payload[i].key = res.payload[i].id;
            }
            res.payload.unshift({ header: 'All Branches', key: 0 });
            setBranches(res.payload);
        }
    }, [company?.id]);

    const loadDepartments = useCallback(async () => {
        const id = company?.id ?? 0;
        const res: any = await getDepartments(id);
        if (res?.payload) {
            for (let i = 0; i < res.payload.length; i++) {
                res.payload[i].header = res.payload[i].name;
                res.payload[i].key = res.payload[i].id;
            }
            res.payload.unshift({ header: 'All Departments', key: 0 });
            setDepartments(res.payload);
        }
    }, [company?.id]);

    const getBranchesDepartment = useCallback((countryIds: any[]) => {
        const filteredUsers: any[] = [];
        const branchNms: string[] = [];
        const newBranches: any[] = [];
        const deptNms: string[] = [];
        const newDepts: any[] = [];

        countryIds.forEach((id) => {
            usersRef.current.forEach((user) => {
                if (user.country?.id == id) filteredUsers.push(user);
            });
        });

        filteredUsers.forEach((user) => {
            if (user.branch && !branchNms.includes(user.branch.name)) {
                branchNms.push(user.branch.name);
                newBranches.push(user.branch);
            }
            if (user.department && !deptNms.includes(user.department.name)) {
                deptNms.push(user.department.name);
                newDepts.push(user.department);
            }
        });

        for (let i = 0; i < newBranches.length; i++) {
            newBranches[i].header = newBranches[i].name;
            newBranches[i].key = newBranches[i].id;
        }
        newBranches.unshift({ header: 'All Branches', key: 0 });
        setBranches(newBranches);

        for (let i = 0; i < newDepts.length; i++) {
            newDepts[i].header = newDepts[i].name;
            newDepts[i].key = newDepts[i].id;
        }
        newDepts.unshift({ header: 'All Departments', key: 0 });
        setDepartments(newDepts);
    }, []);

    const getBranchesByDepartment = useCallback((branchIds: any[]) => {
        const filteredUsers: any[] = [];
        const deptNms: string[] = [];
        const newDepts: any[] = [];

        branchIds.forEach((id) => {
            users_Ref.current.forEach((user) => {
                if (user.branch?.id == id) filteredUsers.push(user);
            });
        });

        filteredUsers.forEach((user) => {
            if (user.department && !deptNms.includes(user.department.name)) {
                deptNms.push(user.department.name);
                newDepts.push(user.department);
            }
        });

        for (let i = 0; i < newDepts.length; i++) {
            newDepts[i].header = newDepts[i].name;
            newDepts[i].key = newDepts[i].id;
        }
        newDepts.unshift({ header: 'All Departments', key: 0 });
        setDepartments(newDepts);
    }, []);

    const filterData = useCallback(
        (method: string, countryNms: string[], brancheNms: string[], deptNms: string[]) => {
            if (method === 'AND') {
                const filtered = usersRef.current.filter(
                    (u: any) =>
                        (countryNms.length === 0 || (u.country && countryNms.includes(u.country.name))) &&
                        (brancheNms.length === 0 || (u.branch && brancheNms.includes(u.branch.name))) &&
                        (deptNms.length === 0 || (u.department && deptNms.includes(u.department.name))),
                );
                setUsers_(filtered);
                setSelectedUsers(filtered);
            } else {
                setUsers_(usersRef.current);
                setSelectedUsers([]);
            }
        },
        [],
    );

    const tableDataGenerator = useCallback((payload: any[]) => {
        const body: any[] = [];
        const newGroups: any[] = [];
        const newGroupedData: GroupedData = {};
        let currentUser = '';

        payload.forEach((shift: any[]) => {
            if (shift.length > 0 && currentUser !== shift[0].userId) {
                currentUser = shift[0].userId;
                newGroups.push({
                    key: shift[0].userId,
                    name: shift[0].userFullName,
                    startIndex: body.length,
                    count: 0,
                    level: 0,
                });
                newGroupedData[shift[0].userId] = {};
            }

            let currentDate = '';
            shift.forEach((clock: any) => {
                if (clock) {
                    const dateKey = localDateKey(clock.time);
                    if (currentDate !== dateKey) {
                        currentDate = dateKey;
                        newGroups.push({
                            key: currentDate,
                            name: currentDate,
                            startIndex: body.length,
                            count: shift.length,
                            level: 1,
                            parentKey: currentUser,
                        });
                        if (!newGroupedData[currentUser][currentDate]) {
                            newGroupedData[currentUser][currentDate] = [];
                        }
                    }
                    if (newGroupedData[currentUser][currentDate]) {
                        newGroupedData[currentUser][currentDate].push(clock);
                    }
                    body.push(clock);
                }
            });
        });

        setItems(body);
        setFlatItems(body);
        setGroups(newGroups);
        setGroupedData(newGroupedData);
    }, []);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setItems([]);
        setGroups([]);
        setOffset(0);
        setFlatOffset(0);

        await getUser(userId);

        let currentUsers = usersRef.current;
        if (currentUsers.length === 0) {
            const usersReq: any = await FetchUsers('Company', company?.id ?? 0, false);
            if (usersReq?.payload) {
                for (let i = 0; i < usersReq.payload.length; i++) {
                    usersReq.payload[i].header = usersReq.payload[i].fullName;
                    usersReq.payload[i].key = usersReq.payload[i].id;
                }
                usersReq.payload.unshift({ header: 'All Users', key: 0 });
                currentUsers = usersReq.payload;
                setUsers(currentUsers);
                setUsers_(currentUsers);
                usersRef.current = currentUsers;
                users_Ref.current = currentUsers;
            }
        }

        if (departments.length === 0) loadDepartments();
        if (branches.length === 0) loadBranches();

        if (countries.length === 0) {
            const countriesReq: any = await getUsersCountries(userId);
            if (countriesReq?.payload) {
                for (let i = 0; i < countriesReq.payload.length; i++) {
                    countriesReq.payload[i].header = countriesReq.payload[i].name;
                    countriesReq.payload[i].key = countriesReq.payload[i].id;
                }
                countriesReq.payload.unshift({ header: 'All Countries', key: 0 });
                setCountries(countriesReq.payload);
            }
        }

        const userIds = selectedUsers.map((u) => u.id);
        const countryIds = selectedCountries.map((c) => c.id);
        const deptIds = selectedDepartments.map((d) => d.id);
        const branchIds = selectedBranches.map((b) => b.id);

        const { payload } = await ReportsApi.getClockingsPerUserPerDay({
            userId,
            from: dateFrom,
            to: dateTo,
            userIds,
            searchMethod,
            departmentIds: deptIds,
            branchIds,
            countryIds,
        });

        tableDataGenerator(payload);
        setLoading(false);
    }, [userId, company?.id, dateFrom, dateTo, selectedUsers, selectedCountries, selectedDepartments, selectedBranches, searchMethod, tableDataGenerator, loadBranches, loadDepartments, countries.length, departments.length, branches.length]);

    useEffect(() => {
        fetchData();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const exportReport = async (format: string) => {
        await ReportsApi.exportClockingsPerUserPerDay(
            {
                userId,
                from: dateFrom,
                to: dateTo,
                userIds: selectedUsers.map((u) => u.id),
                searchMethod,
                departmentIds: selectedDepartments.map((d) => d.id),
                branchIds: selectedBranches.map((b) => b.id),
                countryIds: selectedCountries.map((c) => c.id),
            },
            format,
        );
    };

    const onDateChange = (time: any, type: string) => {
        const d = new Date(time);
        if (type === 'from') setDateFrom(d);
        else setDateTo(d);
    };

    const onChangeUser = (_ev: any, props: any) => {
        const allSelected = props.value.find((i: any) => i.key === 0);
        if (allSelected) {
            setSelectedUsers(users.filter((u) => u.key !== 0));
        } else {
            setSelectedUsers([...props.value]);
        }
    };

    const onChangeCountry = (_ev: any, props: any) => {
        const allSelected = props.value.find((i: any) => i.key === 0);
        if (allSelected) {
            const all = countries.filter((c) => c.key !== 0);
            setSelectedCountries(all);
            setSelectedCountryNames(all.map((c: any) => c.name));
            filterData(searchMethod, all.map((c: any) => c.name), selectedBrancheNames, selectedDepartmentNames);
        } else {
            setSelectedCountries([...props.value]);
            const names = props.value.map((c: any) => c.name);
            setSelectedCountryNames(names);
            filterData(searchMethod, names, selectedBrancheNames, selectedDepartmentNames);
            // BUG FIX: was " AND", now "AND"
            if (searchMethod === 'AND') {
                getBranchesDepartment(props.value.map((c: any) => c.id));
            }
        }
    };

    const onChangeBranch = (_ev: any, props: any) => {
        const allSelected = props.value.find((i: any) => i.key === 0);
        if (allSelected) {
            const all = branches.filter((b) => b.key !== 0);
            setSelectedBranches(all);
            setSelectedBrancheNames([]);
            filterData(searchMethod, selectedCountryNames, [], selectedDepartmentNames);
        } else {
            setSelectedBranches([...props.value]);
            const names = props.value.map((b: any) => b.name);
            setSelectedBrancheNames(names);
            filterData(searchMethod, selectedCountryNames, names, selectedDepartmentNames);
            // BUG FIX: was " AND", now "AND"
            if (searchMethod === 'AND') {
                getBranchesByDepartment(props.value.map((b: any) => b.id));
            }
        }
    };

    const onChangeDepartments = (_ev: any, props: any) => {
        const allSelected = props.value.find((i: any) => i.key === 0);
        if (allSelected) {
            const all = departments.filter((d) => d.key !== 0);
            setSelectedDepartments(all);
            setSelectedDepartmentNames(all.map((d: any) => d.name));
            filterData(searchMethod, selectedCountryNames, selectedBrancheNames, all.map((d: any) => d.name));
        } else {
            setSelectedDepartments([...props.value]);
            const names = props.value.map((d: any) => d.name);
            setSelectedDepartmentNames(names);
            filterData(searchMethod, selectedCountryNames, selectedBrancheNames, names);
        }
    };

    const handleSetSearchMethod = (event: any) => {
        const val = event.target.value ?? 'AND';
        setSearchMethodState(val);
        filterData(val, selectedCountryNames, selectedBrancheNames, selectedDepartmentNames);
        if (selectedCountries.length > 0 && val === 'AND') {
            getBranchesDepartment(selectedCountries.map((c) => c.id));
        } else if (selectedBranches.length > 0 && val === 'AND') {
            getBranchesByDepartment(selectedBranches.map((b) => b.id));
        } else {
            loadBranches();
            loadDepartments();
        }
    };

    // Grouped view
    const userGroups = groups
        .filter((g) => g.level === 0)
        .slice(offset, Math.min(offset + count, groups.filter((g) => g.level === 0).length));

    const firstLevelTotal = groups.filter((g) => g.level === 0).length;
    const groupedTotalPages = Math.max(1, Math.ceil(firstLevelTotal / count));
    const groupedCurrentPage = Math.floor(offset / count) + 1;

    // Flat view
    const flatTotal = flatItems.length;
    const flatPageStart = flatOffset;
    const flatPageEnd = Math.min(flatOffset + rowsPerPageFlat, flatTotal);
    const flatPageRows = flatItems.slice(flatPageStart, flatPageEnd);
    const flatTotalPages = Math.max(1, Math.ceil(flatTotal / rowsPerPageFlat));
    const flatCurrentPage = Math.floor(flatOffset / rowsPerPageFlat) + 1;

    const renderUserGroup = (uid: string, userName: string) => {
        const userClockings = groupedData[uid];
        if (!userClockings) return null;
        const sortedDates = Object.keys(userClockings).sort();
        const totalForUser = sortedDates.reduce((acc, d) => acc + (userClockings[d]?.length || 0), 0);

        return (
            <div key={uid} className="border border-border rounded-lg overflow-hidden mb-4">
                <div
                    className="px-4 py-2 flex items-center justify-between font-bold text-sm text-primary-foreground"
                    style={{ backgroundColor: companyPrimaryColor || 'var(--mowazaf-primary)' }}
                >
                    <span>{userName}</span>
                    <span className="opacity-90 text-xs font-normal">
                        {totalForUser} {t('entries')}
                    </span>
                </div>
                <Table stickyHeader aria-label="clockings grouped table">
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-[160px]">{t('Clock Type')}</TableHead>
                            <TableHead className="w-[120px]">{t('Time')}</TableHead>
                            <TableHead className="w-[160px]">{t('Device')}</TableHead>
                            <TableHead>{t('Address')}</TableHead>
                            <TableHead>{t('Comment')}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {sortedDates.map((date) => {
                            const rows = userClockings[date] || [];
                            const times = rows.map((r: any) => new Date(r.time).getTime()).sort((a: number, b: number) => a - b);
                            const firstTime = times.length ? dateformat(new Date(times[0]), 'h:MM TT') : '';
                            const lastTime = times.length ? dateformat(new Date(times[times.length - 1]), 'h:MM TT') : '';
                            return (
                                <React.Fragment key={`${uid}-${date}`}>
                                    <TableRow>
                                        <TableCell colSpan={5} className="p-0">
                                            <div className="px-3 py-1.5 bg-muted/30 flex items-center justify-between">
                                                <span className="text-sm font-semibold">{date}</span>
                                                <span className="text-xs text-muted-foreground">
                                                    {firstTime && lastTime ? `${firstTime} – ${lastTime}` : ''}{' '}
                                                    {rows.length ? `· ${rows.length} ${t('entries')}` : ''}
                                                </span>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                    {rows.map((item: any, idx: number) => (
                                        <TableRow key={`${uid}-${date}-${idx}`} className={idx % 2 === 0 ? 'bg-muted/20' : ''}>
                                            <TableCell><TypeBadge type={item.type} t={t} /></TableCell>
                                            <TableCell>{dateformat(new Date(item.time), 'h:MM TT')}</TableCell>
                                            <TableCell><DeviceBadge device={item.clientType} t={t} /></TableCell>
                                            <TableCell>
                                                <span className="block truncate max-w-[480px]" title={item.address ?? ''}>
                                                    {item.address ?? ''}
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                <span className="block truncate max-w-[320px]" title={item.comments ?? ''}>
                                                    {item.comments ?? ''}
                                                </span>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            </div>
        );
    };

    return (
        <div className="flex flex-col gap-2.5 p-2.5 flex-1">
            <ReportsController
                users={users}
                users_={users_}
                selectedUsers={selectedUsers}
                onChangeUser={onChangeUser}
                clearAllUsers={() => {
                    setSelectedUsers([]);
                    loadBranches();
                    loadDepartments();
                }}
                dateFrom={dateFrom}
                dateTo={dateTo}
                onDateChange={onDateChange}
                loading={loading}
                exportReport={exportReport}
                fetchData={fetchData}
                setShowFilters={() => setShowFiltersState(!showFilters)}
                showFilters={showFilters}
                countries={countries}
                selectedCountries={selectedCountries}
                onChangeCountry={onChangeCountry}
                clearAllCountries={() => {
                    setSelectedCountries([]);
                    setSelectedCountryNames([]);
                    filterData(searchMethod, [], selectedBrancheNames, selectedDepartmentNames);
                    loadBranches();
                    loadDepartments();
                }}
                branches={branches}
                selectedBranches={selectedBranches}
                onChangeBranches={onChangeBranch}
                clearAllBranches={() => {
                    setSelectedBranches([]);
                    setSelectedBrancheNames([]);
                    filterData(searchMethod, selectedCountryNames, [], selectedDepartmentNames);
                    if (selectedCountries.length > 0 && searchMethod === 'AND') {
                        getBranchesDepartment(selectedCountries.map((c) => c.id));
                    } else {
                        loadBranches();
                        loadDepartments();
                    }
                }}
                departments={departments}
                selectedDepartments={selectedDepartments}
                onChangeDepartments={onChangeDepartments}
                clearAllDepartments={() => {
                    setSelectedDepartments([]);
                    setSelectedDepartmentNames([]);
                    filterData(searchMethod, selectedCountryNames, selectedBrancheNames, []);
                }}
                searchMethod={searchMethod}
                setSearchMethod={handleSetSearchMethod}
                layoutMode={viewMode}
                onLayoutModeChange={(mode) => setViewMode(mode)}
            />

            <div className="h-px bg-border" />

            {loading ? (
                <div className="flex justify-center p-6">
                    <div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
            ) : viewMode === 'flat' ? (
                <>
                    <Table stickyHeader aria-label="clockings flat table">
                        <TableHeader>
                            <TableRow>
                                <TableHead>{t('User')}</TableHead>
                                <TableHead>{t('Day')}</TableHead>
                                <TableHead>{t('Time')}</TableHead>
                                <TableHead>{t('Clock Type')}</TableHead>
                                <TableHead>{t('Device')}</TableHead>
                                <TableHead>{t('Address')}</TableHead>
                                <TableHead>{t('Comment')}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {flatPageRows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                                        {t('common:No data')}
                                    </TableCell>
                                </TableRow>
                            )}
                            {flatPageRows.map((item: any, idx: number) => (
                                <TableRow key={`${item.userId}-${item.time}-${idx}`} className={idx % 2 === 0 ? 'bg-muted/20' : ''}>
                                    <TableCell>{item.userFullName}</TableCell>
                                    <TableCell>{localDateKey(item.time)}</TableCell>
                                    <TableCell>{dateformat(new Date(item.time), 'h:MM TT')}</TableCell>
                                    <TableCell><TypeBadge type={item.type} t={t} /></TableCell>
                                    <TableCell><DeviceBadge device={item.clientType} t={t} /></TableCell>
                                    <TableCell>
                                        <span className="block truncate max-w-[340px]" title={item.address ?? ''}>
                                            {item.address ?? ''}
                                        </span>
                                    </TableCell>
                                    <TableCell>
                                        <span className="block truncate max-w-[240px]" title={item.comments ?? ''}>
                                            {item.comments ?? ''}
                                        </span>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <div className="p-3 flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">
                            {t('Showing')} {flatTotal === 0 ? 0 : flatPageStart + 1}-{flatPageEnd} {t('of')} {flatTotal}
                        </span>
                        <Pagination
                            currentPage={flatCurrentPage}
                            totalPages={flatTotalPages}
                            onPageChange={(newPage) => setFlatOffset((newPage - 1) * rowsPerPageFlat)}
                        />
                    </div>
                </>
            ) : (
                <>
                    <div className="overflow-y-auto">
                        {userGroups.map((group) => renderUserGroup(group.key, group.name))}
                    </div>
                    <div className="p-4">
                        <Pagination
                            currentPage={groupedCurrentPage}
                            totalPages={groupedTotalPages}
                            onPageChange={(newPage) => setOffset((newPage - 1) * count)}
                        />
                    </div>
                </>
            )}
        </div>
    );
}
